<template>
  <div>
    <div class="calculator">
      <div class="calculator__item">
        <CalculatorInput
            :input="cryptoAmount"
            :currencies="cryptoCurrencyList"
            :is-loading="isLoading && !isSelectedAmountCrypto"
            @input-change="handleCryptoAmountChange($event)"
            @option-change="handleCryptoChange($event)"
            @option-is-open="loadCryptoCurrencies()"
            data-testid="cryptofield"
        />
      </div>
      <div class="calculator-equals btn-icon">
        <i class="calculator-equals__icon icon icon-union"></i>
      </div>
      <div class="calculator__item">
        <CalculatorInput
            :input="fiatAmount"
            :currencies="availableFiatCurrencies"
            :is-loading="isLoading && isSelectedAmountCrypto"
            @input-change="handleFiatAmountChange($event)"
            @option-change="handleFiatChange($event)"
            @option-is-open="loadFiatCurrencies()"
            data-testid="fiatfield"
        />
      </div>
    </div>
    <div
        class="calculator__price-hint"
        v-show="isRecalculatedPrice"
    >{{ $t('calculator.recalculatedPriceHint', {
      cryptoName: this.selectedCryptoCurrency.name,
      fiatCode: this.selectedFiatCurrency.code
    }) }}</div>
  </div>
</template>

<script>
import BigNumber from 'bignumber.js';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import CalculatorInput from '@/components/Calculator/CalculatorInput.vue';
import { CalculatorAmount } from '@/models/calculator/calculatorAmount';
import * as constants from '@/models/pricesConstants';
import ws from '@/plugins/cryptoPriceWS';

export default {
  name: 'CalculatorForm',

  components: {
    CalculatorInput,
  },

  data () {
    return {
      defaultCryptoCode: 'BTC',
      defaultFiatCode: 'USD',
      constants: constants,
      isLoading: false,
      priceRequestInterval: null,
      cryptoAmount: CalculatorAmount.createCryptoAmount('0'),
      fiatAmount: CalculatorAmount.createFiatAmount('0'),
    };
  },

  created () {
    const activeInput = this.isSelectedAmountCrypto ? this.cryptoAmount : this.fiatAmount;

    this.setActiveInput(activeInput);
    this.cryptoAmount.setCurrency(this.selectedCryptoCurrency);
    this.fiatAmount.setCurrency(this.selectedFiatCurrency);

    if (this.price === null) {
      return;
    }

    this.calculate();
  },

  beforeMount () {
    ws('calculator', this.selectedMarket.value, this.selectedFiatCurrency.code);
  },

  computed: {
    ...mapGetters('calculator', ['getAvailableCryptoCurrencyByCode']),
    ...mapState('calculator', [
      'price',
      'selectedMarket',
      'selectedFiatCurrency',
      'selectedAmount',
      'selectedCryptoCurrency',
      'availableCryptoCurrencies',
      'availableFiatCurrencies',
      'isSelectedAmountCrypto',
      'wsConnected',
    ]),
    fiatCurrencyList () {
      if (!this.availableFiatCurrencies) {
        return [];
      }

      return this.availableFiatCurrencies;
    },
    cryptoCurrencyList () {
      return this.availableCryptoCurrencies.filter((cryptoCurrency) => {
        return cryptoCurrency.ws && this.selectedMarket.value in cryptoCurrency.ws;
      });
    },
    isRecalculatedPrice () {
      const websocketMarkets = this.selectedCryptoCurrency.ws || false;
      if (!websocketMarkets) {
        return false;
      }
      const websocketFiats = websocketMarkets[this.selectedMarket.value] || [];

      return !websocketFiats.includes(this.selectedFiatCurrency.code);
    },
  },

  watch: {
    price (newPrice) {
      if (newPrice === null) {
        return;
      }
      this.calculate();
    },
  },

  methods: {
    ...mapActions({
      requestPrice: 'calculator/requestPrice',
      requestAvailableCryptos: 'calculator/requestAvailableCryptos',
      requestAvailableFiats: 'calculator/requestAvailableFiats',
    }),
    ...mapMutations({
      setSelectedAmountCrypto: 'calculator/setSelectedAmountCrypto',
      setSelectedAmountFiat: 'calculator/setSelectedAmountFiat',
      useRealtimePrice: 'calculator/useRealtimePrice',
    }),

    handleCryptoAmountChange (cryptoAmount) {
      this.cryptoAmount.amountString = cryptoAmount;
      this.calculateFiat(new BigNumber(cryptoAmount), this.cryptoAmount.currency);
      this.triggerNewConversion();
    },

    handleFiatAmountChange (fiatAmount) {
      this.fiatAmount.amountString = fiatAmount;
      this.calculateCrypto(new BigNumber(fiatAmount), this.fiatAmount.currency);
      this.triggerNewConversion();
    },

    triggerNewConversion () {
      this.$emit('new-conversion', {
        cryptoAmount: this.cryptoAmount.amount,
        cryptoCurrency: this.cryptoAmount.currency,
        fiatCurrency: this.fiatAmount.currency,
      });
    },

    handleFiatChange (fiatCurrency) {
      this.handleRequestPrice(this.selectedCryptoCurrency, fiatCurrency, this.selectedMarket);
      this.$emit('fiat-changed', fiatCurrency);
      this.triggerNewConversion();
    },

    handleCryptoChange (cryptoCurrency) {
      this.handleRequestPrice(cryptoCurrency, this.selectedFiatCurrency, this.selectedMarket);
      this.$emit('crypto-changed', cryptoCurrency);
      this.triggerNewConversion();
    },

    handleRequestPrice (cryptoCurrency, fiatCurrency, market) {
      this.isLoading = true;
      const isFiatChanged = fiatCurrency.code !== this.selectedFiatCurrency.code;
      const isMarketChanged = market.value !== this.selectedMarket.value;
      const changeWebsocket = this.wsConnected && (isFiatChanged || isMarketChanged);
      if (changeWebsocket) {
        this.$disconnect();
      }
      this.requestPrice({ market, cryptoCurrency, fiatCurrency })
        .finally(() => {
          if (changeWebsocket) {
            this.$connectToPrice(market.value, fiatCurrency.code);
          }
          this.isLoading = false;
          this.fiatAmount.currency = fiatCurrency;
          this.cryptoAmount.currency = cryptoCurrency;
        });
    },

    calculate () {
      if (this.isSelectedAmountCrypto) {
        this.calculateFiat(
          this.cryptoAmount.amount,
          this.cryptoAmount.currency,
        );

        return;
      }

      this.calculateCrypto(
        this.fiatAmount.amount,
        this.fiatAmount.currency,
      );
    },

    calculateCrypto (amount, currency) {
      this.fiatAmount.amount = amount;
      this.fiatAmount.setCurrency(currency);
      this.setSelectedAmountFiat(this.fiatAmount.amount);

      const cryptoAmount = this.fiatAmount.amount.dividedBy(this.price);
      this.cryptoAmount.setAmountNumber(cryptoAmount);
      this.startPriceUpdateInterval();
    },

    calculateFiat (amount, currency) {
      this.cryptoAmount.amount = amount;
      this.cryptoAmount.setCurrency(currency);
      this.setSelectedAmountCrypto(this.cryptoAmount.amount);

      const fiatAmount = this.cryptoAmount.amount.times(this.price);
      this.fiatAmount.setAmountNumber(fiatAmount);
      this.startPriceUpdateInterval();
    },

    startPriceUpdateInterval () {
      if (this.$isServer) {
        return;
      }
      if (this.priceRequestInterval) {
        clearInterval(this.priceRequestInterval);
      }
      const intervalTTL = 10 * 1000; // 10 seconds
      this.priceRequestInterval = setInterval(() => {
        this.useRealtimePrice();
      }, intervalTTL);
    },

    setActiveInput (input) {
      input.setAmountNumber(this.selectedAmount);
      input.activate();
    },

    loadCryptoCurrencies () {
      if (this.availableCryptoCurrencies.length > 0) {
        return;
      }

      this.requestAvailableCryptos();
    },

    loadFiatCurrencies () {
      if (this.availableFiatCurrencies.length > 0) {
        return;
      }

      this.requestAvailableFiats();
    },
  },
};
</script>
